/**
 * Vorgangs-Picker (Phase C.2 WP-D1, Postfach-Zuordnung) — Suchfeld + Ergebnisliste für den
 * `auswahl`-Modus des Clearing House ("Welcher Vorgang passt?" / "Anderen Vorgang wählen").
 * Tastatur wie `ui/GlobalSearch.tsx:82-96` (Pfeiltasten + Enter), Debounce aus
 * `hooks/useDebounced.ts` (dieselbe Extraktion, aus der `GlobalSearch` jetzt auch selbst
 * schöpft). Lädt `GET /api/inbox/vorgangssuche` (WP-B) — leer = 12 zuletzt bearbeitete aktive
 * Vorgänge, ab 1 Zeichen eine ILIKE-Suche über Nummer/Anlass/Kundenname. Navigiert selbst NICHT
 * (anders als `GlobalSearch`) — der Aufrufer entscheidet, was eine Auswahl bedeutet (Radio-artige
 * Auswahl im `auswahl`-Modus, kein Seitenwechsel).
 *
 * Design-Ratsche-Vermerk: `Button`/`Input`/`Card`/`CardRow` aus `docs/KONZEPT-DESIGN-KOMPONENTEN.md`
 * sind zum Zeitpunkt dieser Iteration (PR #929) noch nicht gemergt (`git log origin/main` geprüft) —
 * dieses Feld nutzt darum bewusst dieselben Bestandsklassen/Muster wie der Rest von
 * `pages/posteingang.tsx` (kein rohes Auswahlfeld, `Toggle`/`StatusPill` aus `ui/components.tsx`
 * werden bereits verwendet, weil sie schon existieren). Nach dem Merge von #929 hierher migrieren.
 */
import { useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useDebounced } from "../hooks/useDebounced";
import { Icon, StatusPill, Toggle } from "./components";
import { anlassAnzeige, eventStatusMeta } from "./format";

export interface VorgangsPickerTreffer {
  id: string;
  number: string | null;
  occasion: string | null;
  status: string;
  eventDate: string | null;
  guestCount: number | null;
  updatedAt: string;
  customerName: string | null;
}

interface VorgangssucheResponse {
  query: string;
  events: VorgangsPickerTreffer[];
}

export function VorgangsPicker({
  onSelect,
  ausgeschlossen,
  nurAktive = true,
}: {
  onSelect: (treffer: VorgangsPickerTreffer) => void;
  /** Event-IDs, die aus der Ergebnisliste ausgeblendet werden — z.B. die bereits im Abschnitt
   *  "Kandidaten für diesen Absender" gelisteten Vorgänge, damit derselbe Vorgang nicht doppelt
   *  (einmal dort, einmal hier) auftaucht. */
  ausgeschlossen?: string[];
  /** Anfangszustand des "auch abgeschlossene"-Schalters — Vorgabe true (nur aktive Vorgänge). */
  nurAktive?: boolean;
}) {
  const [term, setTerm] = useState("");
  const [alle, setAlle] = useState(!nurAktive);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(term.trim(), 180);

  const path = `/api/inbox/vorgangssuche?q=${encodeURIComponent(debounced)}&alle=${alle ? "1" : "0"}`;
  const { data, loading } = useApi<VorgangssucheResponse>(path);

  const ausgeschlossenSet = useMemo(() => new Set(ausgeschlossen ?? []), [ausgeschlossen]);
  const treffer = useMemo(() => (data?.events ?? []).filter((e) => !ausgeschlossenSet.has(e.id)), [data, ausgeschlossenSet]);

  useEffect(() => setActive(0), [debounced, alle]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, treffer.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && treffer[active]) {
      e.preventDefault();
      onSelect(treffer[active]!);
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-outline-variant/40 dark:border-outline/30">
      <div className="flex items-center gap-2 border-b border-outline-variant/30 px-3 py-2 dark:border-outline/20">
        <Icon name="search" className="text-[16px] text-secondary dark:text-secondary-fixed-dim" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Vorgangsnummer, Anlass oder Kundenname…"
          aria-label="Vorgang suchen"
          className="min-w-0 flex-1 bg-transparent font-body-sm text-[13px] text-on-background outline-none placeholder:text-on-surface-variant/70 dark:text-inverse-on-surface"
        />
        {loading && <Icon name="progress_activity" className="animate-spin text-[15px] text-on-surface-variant" />}
      </div>
      <div className="flex items-center justify-between gap-2 border-b border-outline-variant/30 bg-surface-container-low/60 px-3 py-1.5 dark:border-outline/20 dark:bg-white/[0.02]">
        <span className="font-label-md text-[10.5px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
          {debounced.length > 0 ? "Suchergebnisse" : "Zuletzt bearbeitet"}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">auch abgeschlossene</span>
          <Toggle checked={alle} onChange={setAlle} label="Auch abgeschlossene Vorgänge anzeigen" />
        </div>
      </div>
      <div className="max-h-[220px] overflow-y-auto">
        {!loading && treffer.length === 0 ? (
          <p className="px-3 py-4 text-center font-body-sm text-[12.5px] text-on-surface-variant/70">Keine Treffer.</p>
        ) : (
          treffer.map((t, idx) => (
            <button
              key={t.id}
              type="button"
              onMouseEnter={() => setActive(idx)}
              onClick={() => onSelect(t)}
              className={`flex w-full items-center gap-2 border-b border-outline-variant/10 px-3 py-2 text-left transition-colors last:border-b-0 dark:border-outline/10 ${
                active === idx ? "bg-primary/10" : "hover:bg-surface-container-high dark:hover:bg-white/[0.03]"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <span className="truncate font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                    {anlassAnzeige(t.occasion) ?? "Ohne Anlass"}
                  </span>
                  <StatusPill meta={eventStatusMeta(t.status)} />
                </span>
                <span className="mt-0.5 block truncate font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                  {[t.number, t.customerName].filter(Boolean).join(" · ") || "—"}
                </span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
